/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, warp-drive/no-legacy-request-patterns */
// Extends from
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember imports
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { setDiff } from '@ember/object/computed';
// External imports
import {
  AutoGroupTypes,
  AttendanceParams,
  CalculatedParams,
  JurisdictionalParams,
} from '@linc-technologies/linced-common/enums/group-auto-groups';
import { uniq, without } from '@linc-technologies/linced-common/utils/arrays';
import config from 'linced-admin/config/environment';
import {
  destroyAndSaveRelated,
  loadRelated,
} from 'linced-admin/utils/destroy-record';

export default class AutogroupsController extends Controller {
  // Services
  @service
  fetch;

  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @service
  toaster;

  // Attrs
  AutoGroupTypes = AutoGroupTypes;

  JurisdictionalParams = JurisdictionalParams;
  CalculatedParams = CalculatedParams;
  AttendanceParams = AttendanceParams;

  // Props
  @tracked
  group = null;

  // State
  @tracked
  groupDialogOpen = false;

  @tracked
  saveInProgress = false;

  @tracked
  deleteGroupDialogOpen = false;

  @tracked
  deleteInProgress = false;

  @tracked
  errorMessage = '';

  @tracked
  applyDialogOpen = false;

  @tracked
  applyStep = 0;

  @action
  setApplyStep(value) {
    this.applyStep = value;
  }

  @tracked
  applyTemplates = [];

  @tracked
  applySchools = [];

  @tracked
  applyConfirmed = false;

  @tracked
  applyInProgress = false;

  @tracked
  selectedSchoolGroup = null;

  @action
  setSelectedSchoolGroup(value) {
    this.selectedSchoolGroup = value;
  }

  // Usage sets attributes
  @tracked
  usageSetDialogOpen = false;

  @tracked
  selectedUsageSets = [];

  @tracked
  initialUsageSets = [];

  @setDiff('model.usageSets', 'selectedUsageSetsFilter')
  remainingUsageSetsFilter;

  @tracked
  selectedUsageSetsFilter = [];

  @tracked
  currentPage;

  @computed('model.groups.[]', 'selectedUsageSetsFilter.[]')
  get filteredGroups() {
    return this.get('model.groups')
      .filter((group) => {
        if (!isEmpty(this.selectedUsageSetsFilter)) {
          return this.selectedUsageSetsFilter.some((usageSet) =>
            group.get('usageSetsArray').includes(usageSet),
          );
        }
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  @computed('filteredGroups.@each.grouping')
  get groupings() {
    return [
      ...new Set(
        this.filteredGroups
          .slice()
          .sort((a, b) =>
            a.grouping > b.grouping ? 1 : a.grouping < b.grouping ? -1 : 0,
          )
          .map((group) => group.grouping),
      ),
    ];
  }

  @action
  openApplyDialog() {
    this.applyTemplates = [];
    this.applySchools = [];
    this.applyStep = 0;
    this.applyConfirmed = false;
    this.applyInProgress = false;
    this.applyDialogOpen = true;
    this.selectedSchoolGroup = null;
  }

  @action
  toggleApplyTemplate(id) {
    if (this.applyTemplates.includes(id)) {
      this.applyTemplates = this.applyTemplates.filter((t) => t !== id);
    } else {
      this.applyTemplates = [...this.applyTemplates, id];
    }
  }

  @action
  toggleApplySchool(id) {
    if (this.applySchools.includes(id)) {
      this.applySchools = this.applySchools.filter((s) => s !== id);
    } else {
      this.applySchools = [...this.applySchools, id];
    }
  }

  @action
  selectSchoolGroup() {
    let schoolIds = this.selectedSchoolGroup.schoolsArray.map((s) => s.id);
    this.applySchools = uniq([...this.applySchools, ...schoolIds]);
  }

  @action
  deselectSchoolGroup() {
    let schoolIds = this.selectedSchoolGroup.schoolsArray.map((s) => s.id);
    this.applySchools = this.applySchools.filter(
      (id) => !schoolIds.includes(id),
    );
  }

  @action
  applySelected() {
    this.applyConfirmed = true;
    this.applyInProgress = true;
    let chain = this.applyTemplates.reduce((templatePromise, templateId) => {
      return templatePromise.then(() => {
        let applyUri = `${config.APP.apiHost}/groups/${config.APP.apiPath}/templates/groups/${templateId}/:apply`;
        return this.applySchools.reduce((schoolPromise, schoolId) => {
          let options = {
            data: JSON.stringify({ schoolId }),
          };
          return schoolPromise.then(() => {
            return this.fetch.post(applyUri, options);
          });
        }, Promise.resolve());
      });
    }, Promise.resolve());

    chain.then(() => {
      this.applyInProgress = false;
    });
  }

  @action
  closeApplyDialog() {
    this.applyDialogOpen = false;
  }

  @action
  toggleGroupDialog() {
    this.errorMessage = '';
    this.toggleProperty('groupDialogOpen');
    if (!this.groupDialogOpen) {
      this.resetForm();
    }
  }

  @action
  newGroup() {
    this.group = this.store.createRecord('group', {
      type: 6, // GroupType.AUTO
    });
    this.group.set('params', this.store.createFragment('autogroup-params'));
    this.groupDialogOpen = true;
  }

  @action
  editGroup(group) {
    this.store.findRecord('group', group.id).then((record) => {
      this.group = record;
      this.groupDialogOpen = true;
    });
  }

  @action
  setAutoGroupType(type) {
    this.group.set('autoGroupType', type.value);
    // Reset params
    this.group.set('params', this.store.createFragment('autogroup-params'));
    if (type.value === 2) {
      // Jurisdictional - set jurisdiction to NZ | TODO: change this when we expand to other jurisdictions
      this.group.set('params.jurisdiction', 'NZ');
    }
  }

  @action
  setTypeParam(type) {
    this.group.set('params.type', type.value);
  }

  @action
  addFieldValue(fieldValue) {
    // params.values is a plain array, so reassign rather than mutate in place.
    let values = this.group.get('params.values') ?? [];
    this.group.set('params.values', [...values, fieldValue]);
  }

  @action
  removeFieldValue(fieldValue) {
    let values = this.group.get('params.values') ?? [];
    this.group.set('params.values', without(values, fieldValue));
  }

  @action
  saveGroup() {
    this.saveInProgress = true;
    this.errorMessage = '';
    let action = 'create';

    if (!this.group.get('isNew')) {
      action = 'update';
    }

    this.group
      .save()
      .then(() => {
        this.groupDialogOpen = false;
        this.resetForm();
      })
      .catch((err) => {
        this.handleError(err, action);
      })
      .then(() => {
        this.saveInProgress = false;
      });
  }

  @action
  deleteGroup(group) {
    this.errorMessage = '';
    this.group = group;
    this.deleteGroupDialogOpen = true;
  }

  @action
  async confirmDelete(confirmed) {
    if (!confirmed) {
      // Tidy up / close dialog. Nothing changed, so there is nothing to save.
      this.deleteGroupDialogOpen = false;
      this.group = null;
      return;
    }

    // Deletion confirmed; actually delete the record.
    this.deleteInProgress = true;
    this.errorMessage = '';
    const group = this.group;

    // The group's usage sets get saved after the delete so the server drops
    // the group from them. Gather them first: after the delete the group's
    // relationships read as null (see destroyAndSaveRelated).
    const usageSets = await loadRelated(group, ['usageSetIds']);
    const { deleted, error } = await destroyAndSaveRelated(group, usageSets);
    this.deleteInProgress = false;
    if (!deleted) {
      this.handleError(error, 'delete');
      return;
    }
    if (error) {
      this.toaster.error(
        `The autogroup was deleted, but removing it from its usage sets failed`,
      );
    }

    // Tidy up / close dialog.
    this.deleteGroupDialogOpen = false;
    this.group = null;
  }

  @action
  openUsageSetDialog(group) {
    group
      .get('usageSetIds')
      .then((usageSetsArray) => {
        this.usageSetDialogOpen = true;
        this.group = group;
        this.selectedUsageSets = usageSetsArray.slice();
        this.initialUsageSets = usageSetsArray.slice();
      })
      .catch(() => {
        this.toaster.error(`Error trying to edit the autogroup's usage sets`);
      });
  }

  @action
  closeUsageSetDialog() {
    // The pending save's callbacks still need the state this clears.
    if (this.saveInProgress) {
      return;
    }
    this.usageSetDialogOpen = false;
    this.selectedUsageSets = [];
    this.initialUsageSets = [];
    this.group = null;
  }

  @action
  addUsageSet(usageSet, checked) {
    if (checked) {
      this.selectedUsageSets = [...this.selectedUsageSets, usageSet];
    } else {
      this.selectedUsageSets = this.selectedUsageSets.filter(
        (u) => u !== usageSet,
      );
    }
  }

  @action
  saveGroupAndUsageSet() {
    this.saveInProgress = true;
    let promises = [];
    let changedUsageSets = [];

    // Update the usage sets
    let removedUsageSets = this.initialUsageSets.filter(
      (usageSet) => !this.selectedUsageSets.includes(usageSet),
    );
    let addedUsageSets = this.selectedUsageSets.filter(
      (usageSet) => !this.initialUsageSets.includes(usageSet),
    );
    changedUsageSets = changedUsageSets.concat(
      removedUsageSets,
      addedUsageSets,
    );
    if (!isEmpty(changedUsageSets)) {
      // Usage sets a page has been newly added to/removed from
      changedUsageSets.forEach((changedUsageSet) => {
        promises.push(changedUsageSet.save());
      });
    }

    // Save the page template at the end
    this.group.set('usageSetIds', this.selectedUsageSets);
    promises.push(this.group.save());

    return Promise.all(promises)
      .then(() => {
        // After saving the autogroup and all involved usage sets
        this.toaster.success(`Successfully saved the autogroup and usage sets`);
        this.group.reload();
        this.usageSetDialogOpen = false;
        this.group = null;
        this.selectedUsageSets = [];
        this.initialUsageSets = [];
        this.saveInProgress = false;
      })
      .catch(() => {
        this.toaster.error(`Error saving the autogroup's usage sets`);
        // Put the usage sets back, which also reverts each usage set's side.
        // rollbackAttributes() does not touch relationships.
        this.group.set('usageSetIds', this.initialUsageSets);
        this.group.rollbackAttributes();
        this.saveInProgress = false;
      });
  }

  @action
  addUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = [...this.selectedUsageSetsFilter, usageSet];
  }

  @action
  removeUsageSetFilter(usageSet) {
    this.selectedUsageSetsFilter = this.selectedUsageSetsFilter.filter(
      (u) => u !== usageSet,
    );
  }

  handleError(err) {
    if (!isEmpty(err?.errors?.msg)) {
      this.errorMessage = err.errors.msg;
    } else {
      this.errorMessage = err?.message;
    }
  }

  resetForm() {
    if (this.group.isNew) {
      this.group.deleteRecord();
    }
    this.group = null;
  }

  reset() {
    this.selectedUsageSetsFilter = [];
  }
}
